import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import SchoolsSection from '../components/SchoolsSection';
import EarlyEnrollmentStats from '../components/EarlyEnrollmentStats';
import VideoSection from '../components/VideoSection';
import UrgentCareVideoSection from '../components/UrgentCareVideoSection';
import VideoModal from '../components/VideoModal';
import { formatPrice, getPriceWithEarlyDiscount, formatMonthlyPrice, getMonthlyPriceWithEarlyDiscount } from '../utils/currency';
import {
  Play,
  Shield,
  Heart,
  Users,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Star,
  Video,
  BookOpen,
  Stethoscope,
  Phone,
  MessageCircle,
  Globe,
  Clock,
  DollarSign,
  Target,
  Award,
  Zap,
  Lock,
  Activity,
  BarChart3,
  PieChart,
  LineChart,
  User,
  School,
  Building2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Cell,
  LineChart as RechartsLineChart,
  Line
} from 'recharts';

const Home = () => {
  const [currentProverb, setCurrentProverb] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [currentVideoId, setCurrentVideoId] = useState('JvNI-deDepw');

  // Chart data - 100% coverage at AMHAS Urgent Care Facilities
  const coverageData = [
    { name: 'Outpatient', coverage: 100, color: '#3B82F6', note: 'At AMHAS Urgent Care Facilities' },
    { name: 'Preventive', coverage: 100, color: '#F59E0B', note: 'At AMHAS Urgent Care Facilities' },
    { name: 'Telehealth', coverage: 100, color: '#8B5CF6', note: 'AI + Telehealth in schools' },
    { name: 'Pharmacy', coverage: 100, color: '#EF4444', note: 'At AMHAS Urgent Care Facilities' }
  ];

  const costComparisonData = [
    { service: 'Malaria Visit', without: 5, with: 4, savings: 1 },
    { service: 'Pneumonia Stay', without: 50, with: 32, savings: 18 },
    { service: 'Health Checkup', without: 3, with: 0, savings: 3 },
    { service: 'Telehealth', without: 20, with: 0, savings: 20 }
  ];

  const impactData = [
    { month: 'Jan', attendance: 85, health: 78 },
    { month: 'Feb', attendance: 88, health: 82 },
    { month: 'Mar', attendance: 92, health: 87 },
    { month: 'Apr', attendance: 95, health: 91 },
    { month: 'May', attendance: 97, health: 94 },
    { month: 'Jun', attendance: 98, health: 96 }
  ];

  const proverbs = [
    "The child whose health is guarded today will light the village tomorrow.",
    "When the child is cared for early, the community walks in strength.",
    "A tree watered in its youth shelters the village in its age.",
    "To protect a child's health is to build a nation's future."
  ];

  const pillars = [
    {
      icon: DollarSign,
      title: "Affordable",
      description: "A simple annual premium of $28 USD (15,820 CFA / 2,030 GMD) puts routine care and hospital support within reach of every family.",
      color: "primary"
    },
    {
      icon: Shield,
      title: "Preventive",
      description: "Checkups, vaccinations, school health talks, hygiene and safe-water programs reduce costly emergencies.",
      color: "accent"
    },
    {
      icon: Video,
      title: "Accessible + Telehealth",
      description: "AI + Western telehealth: vitals and symptoms are captured at school, AI proposes a diagnosis, and a U.S./EU/UK doctor reviews and finalizes the care plan. Works even on low bandwidth.",
      color: "secondary"
    },
    {
      icon: Stethoscope,
      title: "Urgent Care Centers",
      description: "World-class urgent care facilities providing 24/7 access to comprehensive medical services, including labs, radiology, pharmacy, and observation beds, all with 100% coverage.",
      color: "primary"
    }
  ];

  const benefits = [
    "Built for schools - Children are easiest to reach where they already are",
    "Designed for budgets - A tiny premium spreads rare, high costs across the community",
    "Prevention first - Early action lowers risk and cost",
    "Choice between urgent care and AI - Receive care at world-class urgent care centers or remotely via AI and telehealth at school",
    "Trust & transparency - Clear benefits, simple claims, visible impact"
  ];

  const impactStats = [
    { number: "100,000", label: "Children Enrolled (Year-1 target)", target: 100000, icon: Users, color: "primary", prefix: "", suffix: "" },
    { number: "200+", label: "Participating Schools", target: 200, icon: BookOpen, color: "accent", prefix: "", suffix: "+" },
    { number: "50+", label: "Clinics & Hospitals in Network", target: 50, icon: Stethoscope, color: "secondary", prefix: "", suffix: "+" },
    { number: "< 30 min", label: "Telehealth Response Time (Median)", target: 30, icon: Clock, color: "primary", prefix: "< ", suffix: " min" },
    { number: "$20-30", label: "Family Savings vs. No Coverage (avg.)", target: 25, icon: DollarSign, color: "accent", prefix: "$", suffix: "" }
  ];

  const howItWorksSteps = [
    { icon: BookOpen, title: "Enroll", description: "Sign up at your school" },
    { icon: DollarSign, title: "Pay", description: `${formatPrice('USD')} (${formatPrice('CFA')} / ${formatPrice('GMD')})` },
    { icon: Shield, title: "ID Card", description: "Receive your AMHAS ID with biometric verification" },
    { icon: Video, title: "Care Delivery", description: "Two pathways: At AMHAS Urgent Care Facilities OR in classroom via AI + Telehealth with remote nurse" },
    { icon: Stethoscope, title: "Coverage Validation", description: "Biometric matching validates coverage at intake, whether at urgent care or school" },
    { icon: Heart, title: "Prevention", description: "Stay healthy through prevention" }
  ];

  const testimonials = [
    {
      quote: "With AMHAS my health is guaranteed.",
      author: "Sakahnyuy Nyuyseni",
      role: "AMHAS Member",
      image: "/Sakahnyuy_Nyuyseni.jpg"
    },
    {
      quote: "My name is Sevedzem. I am 13 years old. I am in Form 3 (third year in secondary school). After my enrollment into AMHAS I have no worries about my healthcare again. I can now concentrate on my studies. I urge other students to get themselves enrolled into AMHAS and they will not regret it.",
      author: "Sevedzem",
      role: "AMHAS Member",
      image: "/Sevedzem.mp4",
      isVideo: true
    },
    {
      quote: "My name is Nyuyfanla. I am 11 years old. I am in Form 1. My enrolment in AMHAS has released my parents from the burden of healthcare worries. I am now a proud Nso [his ethnic group] and an African school child. Thanks to AMHAS.",
      author: "Nyuyfanla",
      role: "AMHAS Member",
      image: "/Nyuyfanla.mp4",
      isVideo: true
    }
  ];


  const costComparison = [
    { service: "Malaria Treatment", without: 15000, with: 3000, savings: 12000 },
    { service: "Pneumonia (1 week)", without: 45000, with: 13500, savings: 31500 },
    { service: "Routine Checkup", without: 8000, with: 0, savings: 8000 },
    { service: "Telehealth Consult", without: 5000, with: 0, savings: 5000 }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentProverb((prev) => (prev + 1) % proverbs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [proverbs.length]);

  return (
    <div className="min-h-screen">
      {/* Hero Section with Video Background */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-gray-800 to-gray-900">
          <div className="relative w-full h-full overflow-hidden">
            <iframe
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full w-auto h-auto"
              src="https://www.youtube-nocookie.com/embed/JrLz8_nHbMs?autoplay=1&loop=1&playlist=JrLz8_nHbMs&mute=1&controls=0&showinfo=0&rel=0&modestbranding=1&iv_load_policy=3&fs=0&disablekb=1&start=0&enablejsapi=0&origin=https://amhas.org"
              title="AMHAS Video Banner"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              referrerPolicy="no-referrer-when-downgrade"
              style={{
                width: '177.77777778vh',
                height: '100vh',
                minWidth: '100vw',
                minHeight: '56.25vw'
              }}
            ></iframe>
          </div>
          <div className="absolute inset-0 bg-black/20"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-shadow-lg">
              Care Today, <span className="netflix-text">Strength</span> <span className="netflix-text">Tomorrow</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-shadow max-w-4xl mx-auto">
              Affordable, school-based care powered by AI + Western telehealth for every child (2–18).
            </p>
            <p className="text-lg mb-8 text-primary-100 text-shadow max-w-5xl mx-auto">
              Care is delivered in two ways: at our world-class AMHAS Urgent Care Facilities or directly in the classroom via AI + Telehealth. At school, a staff member records vitals and symptoms. AI generates an initial diagnosis and care plan; a Western board‑certified doctor reviews and finalizes care. As we scale, urgent care centers are being established while AI and telehealth services expand to all schools.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
              <Link to="/student-enrollment" className="btn-primary text-lg px-8 py-4">
                Enroll Your Child
              </Link>
              <Link to="/for-schools" className="btn-secondary text-lg px-8 py-4">
                For Schools
              </Link>
              <Link to="/investors" className="btn-accent text-lg px-8 py-4">
                Invest / Sponsor Cohorts
              </Link>
            </div>

            {/* Play Video Button */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsVideoPlaying(!isVideoPlaying)}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white px-6 py-3 rounded-full transition-all duration-300"
            >
              <Play size={20} fill="currentColor" />
              <span>Watch Our Story</span>
            </motion.button>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white rounded-full flex justify-center"
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-1 h-3 bg-white rounded-full mt-2"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Proverb Strip */}
      <section className="bg-primary-600 text-white py-4">
        <div className="container-custom">
          <motion.div
            key={currentProverb}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <p className="text-lg font-medium italic">"{proverbs[currentProverb]}"</p>
          </motion.div>
        </div>
      </section>

      {/* Early Sign Up Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Early Sign-Up List</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-4xl mx-auto">
              Join our early sign-up list today! You're not committing to anything - just securing your guaranteed spot
              for when we launch. Early sign-ups receive a <span className="font-bold text-yellow-300">1,200 CFA discount</span> and
              help us determine which regions to launch first.
            </p>
            <p className="text-lg text-primary-200 mb-8 max-w-3xl mx-auto">
              We'll start and scale based on the number of sign-ups. The more people that join from your area,
              the more likely you'll be one of the first locations we launch in as we scale across the country.
              Communities will be grouped by geographic location, and areas with the highest concentration of
              sign-ups get priority launch status.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto"
          >
            {/* Student Enrollment Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/15 transition-all duration-300 flex flex-col h-full">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <User size={40} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Student Sign-Up</h3>
              <p className="text-primary-100 mb-6 flex-grow">
                Individual student enrollment with parent/guardian information. Perfect for families
                who want to secure their child's healthcare coverage.
              </p>
              <div className="mb-6">
                <div className="text-3xl font-bold text-yellow-300 mb-2">
                  {getPriceWithEarlyDiscount('CFA')}
                </div>
                <div className="text-sm text-primary-200 mb-2">
                  <span className="line-through">{formatPrice('CFA')}</span> Early Sign-Up Price
                </div>
                <div className="text-xs text-primary-300 mt-2 pt-2 border-t border-white/20">
                  <div className="mb-1">Monthly: {formatMonthlyPrice('CFA')}</div>
                  <div className="text-primary-200/80">({formatPrice('CFA')} / 12 = {formatMonthlyPrice('CFA')}/month)</div>
                </div>
              </div>
              <a
                href="/student-enrollment"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full py-3 text-lg font-semibold mt-auto"
              >
                Enroll Student
              </a>
            </div>

            {/* School Enrollment Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center hover:bg-white/15 transition-all duration-300 flex flex-col h-full">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <School size={40} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4">School Sign-Up</h3>
              <p className="text-primary-100 mb-6 flex-grow">
                Register your entire school for AMHAS services. Choose to enroll all students
                at once or let families sign up individually.
              </p>
              <div className="mb-6">
                <div className="text-3xl font-bold text-yellow-300 mb-2">
                  {getPriceWithEarlyDiscount('CFA')}
                </div>
                <div className="text-sm text-primary-200 mb-2">
                  <span className="line-through">{formatPrice('CFA')}</span> Early Sign-Up Price (per student)
                </div>
                <div className="text-xs text-primary-300 mt-2 pt-2 border-t border-white/20">
                  <div className="mb-1">Monthly: {formatMonthlyPrice('CFA')}</div>
                  <div className="text-primary-200/80">({formatPrice('CFA')} / 12 = {formatMonthlyPrice('CFA')}/month)</div>
                </div>
              </div>
              <a
                href="/school-enrollment"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full py-3 text-lg font-semibold mt-auto"
              >
                Enroll School
              </a>
            </div>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-6 py-3">
              <CheckCircle size={20} className="text-yellow-300" />
              <span className="text-sm font-medium">
                No commitment • Guaranteed spot • Priority launch • 1,200 CFA discount
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Early Enrollment Stats */}
      <EarlyEnrollmentStats />

      {/* Video Section */}
      <VideoSection
        onVideoClick={(videoId) => {
          setCurrentVideoId(videoId || 'JvNI-deDepw');
          setIsVideoPlaying(true);
        }}
        isModalOpen={isVideoPlaying}
      />

      {/* Urgent Care Image Section */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/urgentcare_front_buildign.png" 
            alt="AMHAS Urgent Healthcare Center" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center py-20"
          >
            <div className="bg-slate-900/85 backdrop-blur-sm rounded-2xl p-12 max-w-4xl mx-auto border border-slate-700/50">
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Stethoscope size={40} className="text-white" />
                </div>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">AMHAS Urgent Healthcare Centers</h2>
              <p className="text-xl md:text-2xl text-white leading-relaxed">
                AMHAS Urgent Healthcare Centers will deliver world-class healthcare to school-age students and the local community, providing 24/7 access to comprehensive medical services through our vertically integrated facilities.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Urgent Care Centers Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center">
                <Stethoscope size={40} className="text-primary-600" />
              </div>
            </div>
            <h2 className="section-title">AMHAS Urgent Healthcare Centers</h2>
            <p className="section-subtitle max-w-4xl mx-auto">
              Our urgent care centers are being established first to provide immediate, reliable healthcare access. As we scale, AI and telehealth services will expand to all schools, ensuring comprehensive care delivery through both pathways.
            </p>
          </motion.div>

          {/* Our Centers */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h3 className="text-3xl font-bold text-center mb-8">Our Centers</h3>
            <p className="text-lg text-gray-700 text-center max-w-3xl mx-auto mb-12">
              Our world-class urgent care centers are engineered from the ground up to deliver Western-standard healthcare in a repeatable, scalable, and vertically integrated model.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Urgent Care & Clinical Treatment",
                  description: "100% coverage for non-emergency medical visits and evaluations at AMHAS Urgent Care Facilities",
                  image: "/urgentcare_doctor_Care_1.png"
                },
                {
                  title: "Laboratory Services",
                  description: "Complete lab work and blood testing with 100% coverage at our facilities",
                  image: "/urgentcare_lab_2.png"
                },
                {
                  title: "Radiology & Diagnostic Imaging",
                  description: "Advanced imaging services including X-rays and diagnostic scans",
                  image: "/urgentcare_front_area_1.png"
                },
                {
                  title: "Pharmacy Services",
                  description: "On-site pharmacy with essential pharmaceuticals and medications",
                  image: "/urgentcare_payments_community.png"
                },
                {
                  title: "Observation Beds",
                  description: "Extended non-surgical treatment and monitoring capabilities",
                  image: "/urgentcare_beds.png"
                },
                {
                  title: "Non-Surgical Emergency Care",
                  description: "Immediate care for urgent but non-surgical medical situations",
                  image: "/urgentcare_nurse_triage_1.png"
                }
              ].map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card overflow-hidden"
                >
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <h4 className="text-xl font-bold mb-3">{service.title}</h4>
                    <p className="text-gray-600">{service.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>


          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link to="/urgent-care" className="btn-primary text-lg px-8 py-4">
              Learn More About Our Urgent Care Centers
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Urgent Care Video Section */}
      <UrgentCareVideoSection
        onVideoClick={(videoId) => {
          setCurrentVideoId(videoId || 'ZQr5Adj9S9o');
          setIsVideoPlaying(true);
        }}
        isModalOpen={isVideoPlaying}
      />

      {/* Implementation Timeline */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Implementation Timeline</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-4">
              AMHAS is strategically rolling out urgent care centers first, followed by AI and telehealth technology. 
              This phased approach ensures care is available as we scale technology deployments, allowing us to control 
              the pace and quality of expansion.
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              Facility placement is based on school and population density, with high-density areas receiving priority 
              to maximize impact and ensure efficient resource allocation.
            </p>
          </motion.div>

          {/* Visual Timeline */}
          <div className="relative max-w-5xl mx-auto mb-16">
            {/* Timeline Line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-primary-600 via-accent-600 to-secondary-600 transform -translate-y-1/2"></div>
            
            {/* Timeline Items */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              {[
                {
                  timeline: "Start + 3 Months",
                  title: "Construction Begins",
                  description: "AMHAS Urgent Healthcare Facilities construction starts in high-density population areas",
                  icon: Building2,
                  color: "primary"
                },
                {
                  timeline: "+6 Months",
                  title: "Facilities Open & ID Cards Issued",
                  description: "Urgent Healthcare Facilities open. AMHAS ID Cards issued to students. Care begins. Initially selected schools receive AI and Telehealth devices for beta run.",
                  icon: CheckCircle,
                  color: "accent"
                },
                {
                  timeline: "+12 Months",
                  title: "Full Rollout",
                  description: "All Urgent Healthcare Facilities open. AI and Telehealth fully rolled out at and around all Urgent Healthcare Facilities.",
                  icon: Target,
                  color: "secondary"
                }
              ].map((milestone, index) => {
                const Icon = milestone.icon;
                return (
                  <motion.div
                    key={milestone.timeline}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    className="relative"
                  >
                    {/* Timeline Dot */}
                    <div className="hidden md:block absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        milestone.color === 'primary' ? 'bg-primary-600' :
                        milestone.color === 'accent' ? 'bg-accent-600' :
                        'bg-secondary-600'
                      } shadow-lg`}>
                        <Icon size={24} className="text-white" />
                      </div>
                    </div>

                    {/* Card */}
                    <div className="card p-6 text-center mt-8 md:mt-16 hover:shadow-xl transition-all duration-300">
                      <div className={`w-16 h-16 md:hidden rounded-full flex items-center justify-center mx-auto mb-4 ${
                        milestone.color === 'primary' ? 'bg-primary-600' :
                        milestone.color === 'accent' ? 'bg-accent-600' :
                        'bg-secondary-600'
                      }`}>
                        <Icon size={32} className="text-white" />
                      </div>
                      <div className={`text-sm font-semibold mb-2 ${
                        milestone.color === 'primary' ? 'text-primary-600' :
                        milestone.color === 'accent' ? 'text-accent-600' :
                        'text-secondary-600'
                      }`}>
                        {milestone.timeline}
                      </div>
                      <h4 className="text-xl font-bold text-gray-900 mb-3">{milestone.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{milestone.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Why Urgent Care First Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="bg-primary-50 rounded-2xl p-8 md:p-12 max-w-4xl mx-auto"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">Why Urgent Care Centers First?</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Immediate Care Availability</h4>
                  <p className="text-gray-700 text-sm">
                    Urgent care centers provide immediate, in-person healthcare from day one, ensuring no gap in care delivery as we scale technology.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Controlled Scaling</h4>
                  <p className="text-gray-700 text-sm">
                    By establishing physical facilities first, we can control the pace of technology deployment, ensuring quality and reliability at every step.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Population-Based Placement</h4>
                  <p className="text-gray-700 text-sm">
                    Facilities are strategically placed in high-density areas first, maximizing impact and ensuring efficient resource allocation based on school and population density.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Technology Integration</h4>
                  <p className="text-gray-700 text-sm">
                    As urgent care centers become operational, AI and telehealth technology is gradually integrated, allowing for seamless expansion and quality assurance.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI & Telehealth Image Section */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/urgentcare_telehealth_classrom.png" 
            alt="AMHAS AI & Telehealth" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center py-20"
          >
            <div className="bg-slate-900/85 backdrop-blur-sm rounded-2xl p-12 max-w-4xl mx-auto border border-slate-700/50">
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <Video size={40} className="text-white" />
                </div>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">AI & Telehealth</h2>
              <p className="text-xl md:text-2xl text-white leading-relaxed">
                AMHAS brings world-class healthcare directly to schools through AI-powered diagnostics and Western board-certified telehealth consultations. Students receive immediate care assessment and guidance without leaving the classroom, ensuring quick access to quality medical support.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Schools Section */}
      <SchoolsSection />

      {/* Three Pillars */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Value in Four Pillars</h2>
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-4">
              Building a healthier future for children through accessible, affordable, preventive healthcare, and world-class urgent care facilities.
            </p>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              AMHAS is built on four foundational pillars that work together to deliver comprehensive, sustainable healthcare for every child. 
              Each pillar addresses a critical aspect of healthcare delivery, ensuring families receive the care they need when they need it, 
              while building healthier communities for generations to come.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {pillars.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="card p-8 text-center hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
                >
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${pillar.color === 'primary' ? 'bg-primary-100' :
                    pillar.color === 'accent' ? 'bg-accent-100' :
                      'bg-secondary-100'
                    }`}>
                    <Icon size={40} className={
                      pillar.color === 'primary' ? 'text-primary-600' :
                        pillar.color === 'accent' ? 'text-accent-600' :
                          'text-secondary-600'
                    } />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{pillar.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-6 flex-grow">{pillar.description}</p>
                  
                  {/* Additional details for each pillar */}
                  {pillar.title === "Affordable" && (
                    <div className="mt-auto pt-4 border-t border-gray-200">
                      <ul className="text-left text-sm text-gray-600 space-y-2">
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-primary-600 mt-1 flex-shrink-0" />
                          <span>One price for all families - no urban/rural distinction</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-primary-600 mt-1 flex-shrink-0" />
                          <span>Early sign-up discount: 1,200 CFA off annual premium</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-primary-600 mt-1 flex-shrink-0" />
                          <span>Less than the cost of a single clinic visit</span>
                        </li>
                      </ul>
                    </div>
                  )}
                  
                  {pillar.title === "Preventive" && (
                    <div className="mt-auto pt-4 border-t border-gray-200">
                      <ul className="text-left text-sm text-gray-600 space-y-2">
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                          <span>Regular health checkups and screenings</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                          <span>School-based health education programs</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                          <span>Vaccination campaigns and deworming</span>
                        </li>
                      </ul>
                    </div>
                  )}
                  
                  {pillar.title === "Accessible + Telehealth" && (
                    <div className="mt-auto pt-4 border-t border-gray-200">
                      <ul className="text-left text-sm text-gray-600 space-y-2">
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-secondary-600 mt-1 flex-shrink-0" />
                          <span>AI-powered diagnostics at school</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-secondary-600 mt-1 flex-shrink-0" />
                          <span>Western board-certified doctor review</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-secondary-600 mt-1 flex-shrink-0" />
                          <span>Works on low bandwidth connections</span>
                        </li>
                      </ul>
                    </div>
                  )}
                  
                  {pillar.title === "Urgent Care Centers" && (
                    <div className="mt-auto pt-4 border-t border-gray-200">
                      <ul className="text-left text-sm text-gray-600 space-y-2">
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-primary-600 mt-1 flex-shrink-0" />
                          <span>24/7 access, 365 days a year</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-primary-600 mt-1 flex-shrink-0" />
                          <span>100% coverage for all services</span>
                        </li>
                        <li className="flex items-start space-x-2">
                          <CheckCircle size={16} className="text-primary-600 mt-1 flex-shrink-0" />
                          <span>Full vertical integration: lab, radiology, pharmacy</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Additional Benefits Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 md:p-12 max-w-5xl mx-auto"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">How Our Pillars Work Together</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Comprehensive Coverage</h4>
                  <p className="text-gray-700 text-sm">
                    The combination of affordable pricing, preventive care, accessible telehealth, and urgent care centers ensures 
                    every child receives the right care at the right time, whether it's routine checkups, urgent needs, or preventive education.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Financial Protection</h4>
                  <p className="text-gray-700 text-sm">
                    Affordable premiums protect families from catastrophic healthcare costs while preventive programs reduce the 
                    frequency and severity of illnesses, creating a sustainable model that benefits everyone.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-secondary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Community Impact</h4>
                  <p className="text-gray-700 text-sm">
                    By focusing on schools and population-dense areas, AMHAS maximizes impact, ensuring the most children 
                    benefit from our services while building healthier communities.
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <TrendingUp size={24} className="text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Scalable Model</h4>
                  <p className="text-gray-700 text-sm">
                    Our phased approach—urgent care centers first, then technology deployment—allows us to scale responsibly, 
                    ensuring quality care is available as we expand our reach.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What AMHAS Does - Quick Overview */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="/urgentcare_doctor_Care_1.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">What AMHAS Does</h2>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Simple steps to comprehensive healthcare coverage
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              {
                icon: BookOpen,
                title: "Enroll at school",
                description: "Parents sign up once a year; children receive an AMHAS ID."
              },
              {
                icon: Stethoscope,
                title: "Use AI for initial diagnosis",
                description: "That a western doctor will utilize to assist in diagnosing improving quality and speed of care."
              },
              {
                icon: Video,
                title: "Tap telehealth first",
                description: "Speak to a Western board-certified doctor for quick guidance, referrals, and second opinions."
              },
              {
                icon: Heart,
                title: "Receive care at urgent care centers",
                description: "Visit one of our world-class urgent healthcare centers for in-person care, or receive care remotely using AI and telehealth at school."
              },
              {
                icon: Shield,
                title: "Prevent, prevent, prevent",
                description: "School programs for hygiene, nutrition, hydration, and exercise keep kids healthy and in class."
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 text-center hover:bg-white transition-all duration-300"
                >
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${item.icon === BookOpen ? 'bg-primary-600' :
                    item.icon === Stethoscope ? 'bg-accent-600' :
                      item.icon === Video ? 'bg-secondary-600' :
                        item.icon === Heart ? 'bg-primary-600' :
                          'bg-accent-600'
                    }`}>
                    <Icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coverage Snapshot with Chart */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Coverage Snapshot</h2>
            <p className="section-subtitle">
              Comprehensive coverage designed to keep children healthy and families financially protected
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Coverage Breakdown</h3>
              <div className="space-y-4">
                {coverageData.map((item, index) => (
                  <div key={item.name} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="font-medium text-gray-900">{item.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all duration-1000"
                            style={{
                              width: `${item.coverage}%`,
                              backgroundColor: item.color
                            }}
                          ></div>
                        </div>
                        <span className="font-bold text-gray-900 w-12 text-right">{item.coverage}%</span>
                      </div>
                    </div>
                    {item.note && (
                      <p className="text-sm text-gray-600 ml-7">{item.note}</p>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-primary-600 to-accent-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Key Features</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircle size={20} className="text-white" />
                    <span>Telehealth: Included; non-emergency advice, triage, treatment guidance, and referrals</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle size={20} className="text-white" />
                    <span>Emergency note: Telehealth is not for emergencies. In an emergency, use local emergency numbers or go to the nearest facility immediately.</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle size={20} className="text-white" />
                    <span>Board-certified physicians (U.S./EU/UK) available for non-emergency video/audio visits</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <CheckCircle size={20} className="text-white" />
                    <span>Low-bandwidth modes: voice-only, chat, and store-and-forward for areas with limited internet</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cost Comparison Chart */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Real Cost Examples</h2>
            <p className="section-subtitle">
              See how much families save with AMHAS coverage
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Cost Comparison</h3>
              <div className="space-y-4">
                {costComparisonData.map((item, index) => (
                  <div key={item.service} className="p-4 bg-white rounded-lg shadow-sm">
                    <h4 className="font-semibold text-gray-900 mb-3">{item.service}</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Without AMHAS:</span>
                        <span className="font-bold text-red-600">${item.without}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">With AMHAS:</span>
                        <span className="font-bold text-green-600">${item.with}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-semibold text-gray-900">Savings:</span>
                        <span className="font-bold text-primary-600">${item.savings}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="h-96"
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={costComparisonData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="service" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="without" fill="#ef4444" name="Without AMHAS" />
                  <Bar dataKey="with" fill="#10b981" name="With AMHAS" />
                </BarChart>
              </ResponsiveContainer>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why AMHAS Works */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Why AMHAS Works</h2>
            <p className="section-subtitle">
              Built on proven principles for sustainable healthcare delivery
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Approach</h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-3"
                  >
                    <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-primary-600 to-accent-600 rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Real-World Benefits</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Heart size={20} className="text-white mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Health</h4>
                      <p className="text-sm text-primary-100">Fewer missed school days; quicker recovery from malaria, diarrhoea, and respiratory infections.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <DollarSign size={20} className="text-white mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Family Finances</h4>
                      <p className="text-sm text-primary-100">Lower out-of-pocket shocks; fewer high-cost emergencies; fewer lost workdays for caregivers.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <BookOpen size={20} className="text-white mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Schools</h4>
                      <p className="text-sm text-primary-100">Higher attendance, better performance, safer hygiene culture.</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Counters */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">Early Targets & Impact</h2>
            <p className="text-xl text-primary-100">
              Dynamic targets that will update as data accrues
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {impactStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon size={32} className="text-white" />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold mb-2">
                    <CountUp
                      start={0}
                      end={stat.target}
                      duration={2.5}
                      delay={index * 0.2}
                      prefix={stat.prefix}
                      suffix={stat.suffix}
                    />
                  </div>
                  <div className="text-primary-100 text-sm">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-8"
          >
            <p className="text-sm text-primary-200 italic">
              *Figures are pilot targets/estimates and will update as data accrues.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Simple steps to protect your child's health
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {howItWorksSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    <Icon size={32} />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-600 text-sm">{step.description}</p>
                  {index < howItWorksSteps.length - 1 && (
                    <ArrowRight className="hidden lg:block absolute top-8 left-full transform -translate-x-1/2 text-primary-300" size={20} />
                  )}
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/how-it-works" className="btn-primary">
              Learn More About Our Process
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">What People Say</h2>
            <p className="section-subtitle">
              Real stories from families and schools
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.author}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                {testimonial.image && testimonial.image !== "/api/placeholder/300/200" ? (
                  testimonial.isVideo ? (
                    <video 
                      src={testimonial.image} 
                      className="w-full h-48 object-cover rounded-lg mb-6"
                      controls
                      loop
                      muted
                    />
                  ) : (
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.author}
                      className="w-full h-48 object-cover rounded-lg mb-6"
                    />
                  )
                ) : null}
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="text-yellow-400 fill-current" />
                  ))}
                </div>
                <blockquote className="text-gray-700 mb-6 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.author}</div>
                  <div className="text-gray-600 text-sm">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Trust, Safety & Privacy */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Trust, Safety & Privacy</h2>
            <p className="section-subtitle">
              Your child's safety and privacy are our top priorities
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: "Child Safeguarding",
                description: "Child safeguarding and consent protocols for telehealth."
              },
              {
                icon: Lock,
                title: "Data Privacy",
                description: "Data privacy for minors; encrypted records; limited access by role."
              },
              {
                icon: Award,
                title: "Clinical Quality",
                description: "Clinical quality: guidelines, peer review, and case audits for both telehealth and in-person care."
              },
              {
                icon: CheckCircle,
                title: "Clear Exclusions",
                description: "Clear exclusions to keep premiums low and transparent (chronic/terminal illnesses, major surgeries, >3-month stays, pandemic illnesses)."
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-6 text-center"
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon size={32} className="text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final Call-to-Action */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Health planted in childhood becomes the harvest of tomorrow.</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Join thousands of families who have already enrolled their children in AMHAS.
              Get access to Western board-certified doctors via AI + telehealth, with referral to local clinics only for procedures, and preventive care programs.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/student-enrollment" className="btn-secondary text-lg px-8 py-4">
                Enroll Now
              </Link>
              <Link to="/for-schools" className="btn-secondary text-lg px-8 py-4">
                For Schools
              </Link>
              <Link to="/investors" className="btn-secondary text-lg px-8 py-4">
                Invest / Sponsor Children
              </Link>
            </div>

            <div className="mt-8 text-sm text-primary-200">
              <p>AMHAS telehealth provides non-emergency medical advice and care coordination. Services are delivered in accordance with applicable laws and may vary by location. In emergencies, call local emergency services or go to the nearest facility.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoPlaying}
        onClose={() => setIsVideoPlaying(false)}
        videoId={currentVideoId}
      />
    </div>
  );
};

export default Home;